/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";

import { clearCart, setLayout, useMaterialUIController } from "context";
import CommonStoreHeader from "components/CommonStoreHeader";
import CommonFooter from "components/CommonFooter";
import AppLoader from "components/AppLoader";
import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import productPlaceholder from "assets/images/product-placeholder.svg";

const toSafeNumber = (value) => {
  const parsed = typeof value === "string" ? Number(value.replace(/[^0-9.-]/g, "")) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) =>
  `Rs ${toSafeNumber(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

function CheckoutConfirmation() {
  const [, dispatch] = useMaterialUIController();
  const [isLoading, setIsLoading] = useState(true);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [success, setSuccess] = useState("");
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [confirmation, setConfirmation] = useState({
    contact: { name: "", email: "", phoneNumber: "", billingAddress: "" },
    items: [],
    itemCount: 0,
    subTotal: 0,
    deliveryFee: 0,
    totalAmount: 0,
  });

  useEffect(() => {
    setLayout(dispatch, "page");
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const loadConfirmation = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const response = await fetchWithAuth(
          `${appSettings.baseUrl}${appSettings.endpoints.getCheckoutConfirmation}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error("Failed to load checkout confirmation.");
        }

        const data = await response.json();
        if (!isMounted) return;

        const items = Array.isArray(data?.items) ? data.items : [];
        const mappedItems = items.map((item) => {
          const rawImage =
            Array.isArray(item?.imageUrls) && item.imageUrls.length > 0 ? item.imageUrls[0] : "";
          const image =
            rawImage && (rawImage.startsWith("http://") || rawImage.startsWith("https://"))
              ? rawImage
              : rawImage
              ? `${appSettings.baseUrl}${rawImage.startsWith("/") ? rawImage : `/${rawImage}`}`
              : "";

          return {
            listingId: Number(item?.listingId || 0),
            title: item?.title || "Unnamed Item",
            quantity: Math.max(1, toSafeNumber(item?.quantity)),
            price: toSafeNumber(item?.price),
            image,
          };
        });

        const itemCount = mappedItems.reduce((total, item) => total + item.quantity, 0);
        const subtotal = mappedItems.reduce((total, item) => total + item.price * item.quantity, 0);

        setConfirmation({
          contact: {
            name: data?.contact?.name || "",
            email: data?.contact?.email || "",
            phoneNumber: data?.contact?.phoneNumber || "",
            billingAddress: data?.contact?.billingAddress || "",
          },
          items: mappedItems,
          itemCount: Number(data?.itemCount || itemCount),
          subTotal: toSafeNumber(data?.itemTotal ?? data?.subTotal ?? subtotal),
          deliveryFee: toSafeNumber(data?.deliveryFee),
          totalAmount: toSafeNumber(
            data?.grandTotal ??
              data?.totalAmount ??
              toSafeNumber(data?.itemTotal ?? data?.subTotal ?? subtotal) +
                toSafeNumber(data?.deliveryFee)
          ),
        });
      } catch (loadError) {
        if (isMounted) {
          setLoadError("Unable to load checkout details. Please try again.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadConfirmation();
    return () => {
      isMounted = false;
    };
  }, []);

  const hasItems = useMemo(() => confirmation.items.length > 0, [confirmation.items]);

  const handlePlaceOrder = async () => {
    try {
      setIsPlacingOrder(true);
      setActionError("");
      setSuccess("");

      const response = await fetchWithAuth(
        `${appSettings.baseUrl}${appSettings.endpoints.placeOrderFromCart}`,
        { method: "POST" }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to place order.");
      }

      setSuccess(
        `Order placed successfully. Order ID: ${payload?.orderId || "-"}. Invoice: ${
          payload?.invoiceFileName || "-"
        }`
      );
      setInvoiceUrl(payload?.invoiceUrl || "");
      clearCart(dispatch);
      setConfirmation((prev) => ({
        ...prev,
        items: [],
        itemCount: 0,
        subTotal: 0,
        totalAmount: 0,
      }));
    } catch (placeError) {
      setActionError(placeError.message || "Failed to place order.");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f3f4f6" }}>
      <CommonStoreHeader />

      <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 }, py: 3 }}>
        <Typography
          sx={{ fontWeight: 900, fontSize: { xs: "1.35rem", md: "1.8rem" }, color: "#7f1d1d" }}
        >
          Checkout Confirmation
        </Typography>
        <Typography sx={{ color: "#64748b", mt: 0.4, mb: 2.2 }}>
          Confirm your contact details and cart prices before placing the order.
        </Typography>

        {isLoading ? (
          <Box
            sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <AppLoader size={42} />
          </Box>
        ) : loadError ? (
          <Card sx={{ p: 2.2, borderRadius: 2, border: "1px solid #fecaca" }}>
            <Typography sx={{ color: "#b91c1c", fontWeight: 700 }}>{loadError}</Typography>
          </Card>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(280px, 1fr)" },
              gap: 2,
            }}
          >
            <Stack spacing={2}>
              <Card sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontWeight: 800, color: "#7f1d1d", mb: 1.2 }}>
                  Contact Information
                </Typography>
                <Stack spacing={0.8}>
                  <Typography>
                    <strong>Name:</strong> {confirmation.contact.name || "-"}
                  </Typography>
                  <Typography>
                    <strong>Email:</strong> {confirmation.contact.email || "-"}
                  </Typography>
                  <Typography>
                    <strong>Phone:</strong> {confirmation.contact.phoneNumber || "-"}
                  </Typography>
                  <Typography>
                    <strong>Address:</strong> {confirmation.contact.billingAddress || "-"}
                  </Typography>
                </Stack>
              </Card>

              <Card sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontWeight: 800, color: "#7f1d1d", mb: 1.2 }}>
                  Items from Cart
                </Typography>
                {!hasItems ? (
                  <Typography sx={{ color: "#64748b" }}>No cart items found.</Typography>
                ) : (
                  <Stack spacing={1.1}>
                    {confirmation.items.map((item) => (
                      <Stack
                        key={`checkout-item-${item.listingId}-${item.title}`}
                        direction="row"
                        spacing={1.2}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ border: "1px solid #eef2f7", borderRadius: 1.5, p: 1.1 }}
                      >
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Box
                            component="img"
                            src={item.image || productPlaceholder}
                            alt={item.title}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = productPlaceholder;
                            }}
                            sx={{ width: 58, height: 58, borderRadius: 1.2, objectFit: "cover" }}
                          />
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>
                            <Typography sx={{ color: "#64748b", fontSize: "0.88rem" }}>
                              Qty: {item.quantity}
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                          {formatCurrency(item.price * item.quantity)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Card>
            </Stack>

            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #fecdd3",
                boxShadow: "0 14px 30px rgba(183, 28, 42, 0.12)",
                height: "fit-content",
                position: { lg: "sticky" },
                top: { lg: 132 },
              }}
            >
              {actionError && (
                <Alert severity="error" sx={{ mb: 1.1 }}>
                  {actionError}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 1.1 }}>
                  {success}
                </Alert>
              )}
              {invoiceUrl && (
                <Button
                  component="a"
                  href={invoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 1.1, borderColor: "#b71c2a", color: "#b71c2a", fontWeight: 700 }}
                >
                  View Invoice
                </Button>
              )}
              <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", color: "#7f1d1d" }}>
                Order Summary
              </Typography>
              <Divider sx={{ my: 1.3, borderColor: "#fecdd3" }} />
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: "#64748b", fontWeight: 600 }}>
                    Items ({confirmation.itemCount})
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatCurrency(confirmation.subTotal)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: "#64748b", fontWeight: 600 }}>Delivery</Typography>
                  <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                    {formatCurrency(confirmation.deliveryFee)}
                  </Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 1.3, borderColor: "#fecdd3" }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontWeight: 800 }}>Total</Typography>
                <Typography sx={{ fontWeight: 900, color: "#7f1d1d", fontSize: "1.15rem" }}>
                  {formatCurrency(confirmation.totalAmount)}
                </Typography>
              </Stack>

              <Button
                variant="contained"
                fullWidth
                onClick={handlePlaceOrder}
                disabled={!hasItems || isPlacingOrder}
                sx={{
                  mt: 2,
                  py: 1.1,
                  borderRadius: 1.4,
                  bgcolor: "#b71c2a",
                  color: "#ffffff !important",
                  fontWeight: 800,
                  "&:hover": { bgcolor: "#991b1b" },
                }}
              >
                {isPlacingOrder ? "PLACING ORDER..." : "CONFIRM & PLACE ORDER"}
              </Button>
              <Button
                component={Link}
                to="/cart"
                variant="text"
                fullWidth
                sx={{ mt: 0.6, color: "#7f1d1d", fontWeight: 700 }}
              >
                Back to Cart
              </Button>
            </Card>
          </Box>
        )}
      </Box>

      <CommonFooter />
    </Box>
  );
}

export default CheckoutConfirmation;
